/* Lyxburken — behaviour for the Home and Product templates.
   Two small pieces: an AJAX add-to-cart that keeps the header bag count in
   sync, and a thumbnail swap for products with more than one photo.
   Both degrade to a plain form POST / a single image without JS. */

(function () {
  'use strict';

  const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
  const cartAddUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';

  function updateCartCount(count) {
    document.querySelectorAll('[data-lyx-cart-count]').forEach((badge) => {
      badge.textContent = count;
      badge.hidden = count === 0;
    });
  }

  const cartToast = document.getElementById('LyxCartToast');
  let cartToastHideTimer = null;
  let cartToastRemoveTimer = null;

  function showCartToast() {
    if (!cartToast) return;

    /* Clearing pending timers means a second add-to-cart within the
       display window restarts the clock instead of the first timer
       hiding the toast out from under the second confirmation. */
    window.clearTimeout(cartToastHideTimer);
    window.clearTimeout(cartToastRemoveTimer);

    cartToast.hidden = false;
    /* Removing [hidden] and adding the transition class in the same tick
       gives the browser nothing to transition from — it just snaps to the
       end state. The extra frame is what makes it a slide-in. */
    requestAnimationFrame(() => {
      requestAnimationFrame(() => cartToast.classList.add('is-visible'));
    });

    cartToastHideTimer = window.setTimeout(() => {
      cartToast.classList.remove('is-visible');
      cartToastRemoveTimer = window.setTimeout(() => {
        cartToast.hidden = true;
      }, 300);
    }, 2200);
  }

  function refreshCartCount() {
    return fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .then((cart) => updateCartCount(cart.item_count))
      .catch(() => {
        /* The count is cosmetic — a stale badge is better than a thrown error. */
      });
  }

  class LyxProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.button = this.querySelector('[data-lyx-add]');
      this.label = this.querySelector('[data-lyx-add-label]');
      this.error = this.querySelector('[data-lyx-error]');
      this.variantSelect = this.querySelector('[data-lyx-variant-select]');
      this.qtyInput = this.querySelector('[data-lyx-qty-input]');
      this.qtyMinus = this.querySelector('[data-lyx-qty-minus]');
      this.qtyPlus = this.querySelector('[data-lyx-qty-plus]');

      if (this.form) this.form.addEventListener('submit', this.onSubmit.bind(this));
      if (this.variantSelect) {
        this.variantSelect.addEventListener('change', this.onVariantChange.bind(this));
      }
      if (this.qtyMinus) this.qtyMinus.addEventListener('click', () => this.stepQty(-1));
      if (this.qtyPlus) this.qtyPlus.addEventListener('click', () => this.stepQty(1));
    }

    stepQty(delta) {
      if (!this.qtyInput) return;
      const min = parseInt(this.qtyInput.min, 10) || 1;
      const next = (parseInt(this.qtyInput.value, 10) || min) + delta;
      this.qtyInput.value = Math.max(min, next);
    }

    onVariantChange() {
      const option = this.variantSelect.selectedOptions[0];
      if (!option) return;

      const price = document.querySelector('[data-lyx-price]');
      const compare = document.querySelector('[data-lyx-compare]');

      if (price && option.dataset.price) price.textContent = option.dataset.price;
      if (compare) {
        compare.textContent = option.dataset.compare || '';
        compare.hidden = !option.dataset.compare;
      }
    }

    onSubmit(event) {
      if (!this.button) return;

      event.preventDefault();
      if (this.button.getAttribute('aria-busy') === 'true') return;

      this.setError('');
      this.setBusy(true, this.dataset.addingLabel);

      fetch(cartAddUrl, {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: new FormData(this.form),
      })
        .then((response) => response.json().then((body) => ({ ok: response.ok, body })))
        .then(({ ok, body }) => {
          if (!ok) throw new Error(body.description || body.message);

          if (this.dataset.afterAdd === 'cart') {
            window.location = cartUrl;
            return;
          }

          if (this.qtyInput) this.qtyInput.value = this.qtyInput.min || 1;
          showCartToast();
          return refreshCartCount().then(() => this.flashSuccess());
        })
        .catch((error) => {
          this.setBusy(false);
          this.setError(error.message);
        });
    }

    flashSuccess() {
      this.setBusy(true, this.dataset.addedLabel);
      window.setTimeout(() => this.setBusy(false), 2000);
    }

    setBusy(busy, label) {
      this.button.setAttribute('aria-busy', busy ? 'true' : 'false');
      this.button.disabled = busy;

      if (!this.label) return;
      if (busy && label) {
        if (this.defaultLabel === undefined) this.defaultLabel = this.label.textContent;
        this.label.textContent = label;
      } else if (!busy && this.defaultLabel !== undefined) {
        this.label.textContent = this.defaultLabel;
      }
    }

    setError(message) {
      if (!this.error) return;
      this.error.textContent = message || '';
      this.error.hidden = !message;
    }
  }

  if (!customElements.get('lyx-product-form')) {
    customElements.define('lyx-product-form', LyxProductForm);
  }

  /* Read by the scroll-hide handler below, so an open phone menu can never
     slide off the top of the screen out from under someone reading it. */
  let lyxNavMenuOpen = false;

  /* Phone navigation disclosure. The links are a plain flex row on desktop
     where the button is hidden, so this only ever runs on a narrow screen. */
  document.querySelectorAll('[data-lyx-nav-toggle]').forEach((toggle) => {
    const links = document.getElementById(toggle.getAttribute('aria-controls'));
    if (!links) return;

    function setOpen(open) {
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      links.classList.toggle('is-open', open);
      lyxNavMenuOpen = open;
    }

    toggle.addEventListener('click', () => {
      setOpen(toggle.getAttribute('aria-expanded') !== 'true');
    });

    /* Following a link should close the panel behind you — the anchor links
       (#om) do not reload the page, so nothing else would. */
    links.addEventListener('click', (event) => {
      if (event.target.closest('a')) setOpen(false);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      if (toggle.getAttribute('aria-expanded') !== 'true') return;
      setOpen(false);
      toggle.focus();
    });
  });

  /* Hide-on-scroll header, phones only. `position: sticky` already keeps it
     pinned — this just slides the whole stuck band off-screen on the way
     down (giving a small screen its vertical space back) and straight back
     on the very next upward pixel, which is what makes it feel responsive
     rather than laggy. */
  (function () {
    const headerEl = document.querySelector('.lyxburken-header-section');
    if (!headerEl) return;
    if (!document.querySelector('.lyx-header--sticky')) return;

    const isPhoneWidth = () => window.matchMedia('(max-width: 699px)').matches;

    let lastScrollY = window.scrollY;
    let ticking = false;

    function onScroll() {
      const currentY = window.scrollY;

      if (!isPhoneWidth() || lyxNavMenuOpen) {
        headerEl.classList.remove('lyx-header-hidden');
      } else if (currentY > lastScrollY && currentY > headerEl.offsetHeight) {
        /* Scrolling down, and past the header's own height — hiding it
           while it is still the only thing on screen would just make the
           page flash empty for a moment. */
        headerEl.classList.add('lyx-header-hidden');
      } else if (currentY < lastScrollY) {
        headerEl.classList.remove('lyx-header-hidden');
      }

      lastScrollY = currentY;
      ticking = false;
    }

    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(onScroll);
      },
      { passive: true }
    );
  })();

  /* Thumbnail gallery — swaps the source of the main product image. */
  document.querySelectorAll('[data-lyx-gallery]').forEach((gallery) => {
    const main = document.getElementById(gallery.dataset.mainImage);
    if (!main) return;

    gallery.addEventListener('click', (event) => {
      const thumb = event.target.closest('[data-lyx-thumb]');
      if (!thumb) return;

      main.removeAttribute('srcset');
      main.src = thumb.dataset.full;
      main.alt = thumb.dataset.alt || '';

      gallery.querySelectorAll('[data-lyx-thumb]').forEach((other) => {
        other.setAttribute('aria-current', other === thumb ? 'true' : 'false');
      });
    });
  });
})();
